// ---------------------------------------------------------------------------
// HoverLabel — shows a small label with the element name/type near the
// hovered element on the canvas. Framer/Figma-style hover identification.
//
// z-index: 45 — above lasso (40), below selection overlay (50).
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { resolveVisibleElement } from '@/features/page-editor/lib/dom-utils'
import { findElementInTree } from '@/features/page-editor/store/canvas-slice'

interface LabelPos {
  top: number
  left: number
  label: string
  type: string
}

const LABEL_HEIGHT = 18

export function HoverLabel() {
  const hoveredElementId = useEditorStore((s) => s.hoveredElementId)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const blocks = useEditorStore((s) => s.blocks)
  const isPreview = useEditorStore((s) => s.isPreview)
  const activeBreakpoint = useEditorStore((s) => s.activeBreakpoint)
  const zoom = useEditorStore((s) => s.zoom)
  const containerRef = useRef<HTMLDivElement>(null)
  const [labelPos, setLabelPos] = useState<LabelPos | null>(null)

  const updateLabel = useCallback(() => {
    // Don't show label for selected elements (they have their own UI)
    if (!hoveredElementId || selectedIds.includes(hoveredElementId)) {
      setLabelPos(null)
      return
    }

    const canvasEl = containerRef.current?.closest('[data-testid="canvas-viewport"]')
    if (!canvasEl) return

    const domEl = canvasEl.querySelector(
      `[data-element-id="${hoveredElementId}"]`,
    ) as HTMLElement | null
    if (!domEl) {
      setLabelPos(null)
      return
    }

    // Resolve to visible element for display:contents wrappers
    const visibleEl = resolveVisibleElement(domEl)

    // Use overlay container rect (not canvasEl) since overlays are positioned
    // inside <div className="relative"> within DeviceFrame, not the canvas viewport.
    // getBoundingClientRect() returns screen-space (post-zoom-transform) values,
    // but CSS top/left operate in local (pre-transform) space, so divide by zoom.
    const containerRect = containerRef.current!.getBoundingClientRect()
    const elRect = visibleEl.getBoundingClientRect()
    const z = zoom || 1

    // Find the element data to get the type
    let type = ''
    const blockEl = domEl.closest('[data-block-name]')
    const blockName = blockEl?.getAttribute('data-block-name')
    if (blockName && blocks[blockName]) {
      const el = findElementInTree(blocks[blockName].ui.root, hoveredElementId)
      if (el) type = el.type
    }

    // Determine label text: show ID if it's human-readable, otherwise show type
    const isGenerated = /^el-[0-9a-f]{8}/.test(hoveredElementId)
    const label = isGenerated ? type || hoveredElementId : hoveredElementId

    // Position label above element; if no room above, show inside at top
    const relTop = (elRect.top - containerRect.top) / z
    const relLeft = (elRect.left - containerRect.left) / z
    const aboveTop = relTop - LABEL_HEIGHT - 2
    const top = aboveTop >= 0 ? aboveTop : relTop + 2

    // Clamp left so label doesn't overflow container right edge
    const maxLeft = containerRef.current!.clientWidth - 80 // rough min visible width
    const left = Math.max(0, Math.min(relLeft, maxLeft))

    setLabelPos({ top, left, label, type })
  }, [hoveredElementId, selectedIds, blocks, activeBreakpoint, zoom])

  useEffect(() => {
    updateLabel()
  }, [updateLabel])

  // Always render container so containerRef is available for DOM queries
  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 overflow-visible"
      style={{ zIndex: 45 }}
      data-testid="hover-label"
    >
      {!isPreview && labelPos && (
        <div
          className="absolute flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none whitespace-nowrap"
          style={{
            top: labelPos.top,
            left: labelPos.left,
            backgroundColor: 'hsl(var(--editor-hover) / 0.85)',
            color: 'hsl(var(--primary-foreground))',
          }}
        >
          {labelPos.type && labelPos.label !== labelPos.type && (
            <span className="opacity-70">{labelPos.type}</span>
          )}
          <span>{labelPos.label}</span>
        </div>
      )}
    </div>
  )
}
